/**
 * SitemapPage — `/sitemap`.
 *
 * Directory of every authenticated route in the staff console. The customer
 * portal and public legal pages live in `apps/web-customer` and are not
 * cross-linked here.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../shared/StaffUI';

interface RouteEntry {
  readonly name: string;
  readonly path: string;
  readonly description?: string;
}

interface RouteGroup {
  readonly group: string;
  readonly pages: readonly RouteEntry[];
}

const SITEMAP: readonly RouteGroup[] = [
  {
    group: 'Dashboard',
    pages: [
      { name: 'Operations dashboard', path: '/', description: 'Real-time KPIs and queues' },
      { name: 'Approvals', path: '/approvals', description: 'Pending supervisor / dual-control approvals' },
    ],
  },
  {
    group: 'Mail',
    pages: [
      { name: 'Mail dashboard', path: '/mail' },
      { name: 'Register mail', path: '/mail/register' },
      { name: 'Bulk registration', path: '/mail/register/bulk' },
      { name: 'My drafts', path: '/mail/drafts' },
      { name: 'All items', path: '/mail/items' },
      { name: 'Track mail', path: '/mail/track' },
      { name: 'Sorting workflow', path: '/mail/sorting' },
      { name: 'Cross-dock manifests', path: '/mail/manifests' },
      { name: 'Returns', path: '/mail/returns' },
      { name: 'Damages', path: '/mail/damages' },
      { name: 'Counter payment', path: '/mail/payments/counter' },
      { name: 'Cash reconciliation', path: '/mail/cash-recon' },
    ],
  },
  {
    group: 'Delivery & Fleet',
    pages: [
      { name: 'Delivery dashboard', path: '/delivery' },
      { name: 'Routes', path: '/delivery/routes' },
      { name: 'Assignment', path: '/delivery/assignment' },
      { name: 'Drivers', path: '/delivery/drivers' },
      { name: 'POD review', path: '/delivery/pod' },
      { name: 'Fleet dashboard', path: '/fleet' },
    ],
  },
  {
    group: 'Customer service',
    pages: [
      { name: 'Dashboard', path: '/customer-service' },
      { name: 'Customer lookup', path: '/customer-service/lookup' },
      { name: 'Complaints', path: '/customer-service/complaints' },
      { name: 'Segments', path: '/customer-service/segments' },
      { name: 'Escalations', path: '/customer-service/escalations' },
    ],
  },
  {
    group: 'Franchise',
    pages: [
      { name: 'Dashboard', path: '/franchise' },
      { name: 'Applications', path: '/franchise/applications' },
      { name: 'Hierarchy', path: '/franchise/hierarchy' },
      { name: 'National map', path: '/franchise/national-map' },
      { name: 'Settlements', path: '/franchise/settlements' },
      { name: 'Performance', path: '/franchise/performance' },
    ],
  },
  {
    group: 'Financial services & KYC',
    pages: [
      { name: 'Financial services dashboard', path: '/financial-services' },
      { name: 'POS', path: '/financial-services/pos' },
      { name: 'Money transfers', path: '/financial-services/transfers' },
      { name: 'Bill payments', path: '/financial-services/bill-payments' },
      { name: 'KYC dashboard', path: '/kyc' },
      { name: 'KYC verify', path: '/kyc/verify' },
    ],
  },
  {
    group: 'Government & Service desk',
    pages: [
      { name: 'Government dashboard', path: '/government' },
      { name: 'Citizen lookup', path: '/government/citizen-lookup' },
      { name: 'Service desk', path: '/service-desk' },
      { name: 'Help centre', path: '/service-desk/help-center' },
    ],
  },
  {
    group: 'Reporting',
    pages: [
      { name: 'Reporting dashboard', path: '/reporting' },
      { name: 'Dashboard builder', path: '/reporting/dashboards/builder' },
      { name: 'Report builder', path: '/reporting/reports/builder' },
      { name: 'Scheduled reports', path: '/reporting/scheduled' },
      { name: 'Ad-hoc query', path: '/reporting/adhoc' },
      { name: 'Management KPIs', path: '/reporting/management-kpis' },
      { name: 'All reports', path: '/reports' },
    ],
  },
  {
    group: 'Settings',
    pages: [
      { name: 'Settings hub', path: '/settings' },
      { name: 'System parameters', path: '/settings/system-parameters' },
      { name: 'Tier service matrix', path: '/settings/tier-service-matrix' },
      { name: 'Pricing', path: '/settings/pricing-config' },
      { name: 'Discount rules', path: '/settings/discount-rules' },
      { name: 'Fraud rules', path: '/settings/fraud-rules' },
      { name: 'Transaction limits', path: '/settings/transaction-limits' },
      { name: 'SLA configuration', path: '/settings/sla-config' },
      { name: 'Government service config', path: '/settings/gov-service-config' },
      { name: 'Outlet management', path: '/settings/outlet-management' },
      { name: 'Notification templates', path: '/settings/notification-templates' },
      { name: 'Recipient accounts', path: '/settings/recipient-accounts' },
      { name: 'Revenue sharing', path: '/settings/revenue-sharing' },
    ],
  },
  {
    group: 'Integration',
    pages: [
      { name: 'Integration dashboard', path: '/integration' },
      { name: 'Health monitor', path: '/integration/health' },
      { name: 'API keys', path: '/integration/api-keys' },
      { name: 'Webhooks', path: '/integration/webhooks' },
      { name: 'Partner onboarding', path: '/integration/partners' },
      { name: 'NIBSS', path: '/integration/nibss' },
    ],
  },
  {
    group: 'Admin',
    pages: [
      { name: 'Email templates', path: '/admin/email-templates' },
      { name: 'Notification admin', path: '/admin/notifications' },
      { name: 'MVP acceptance', path: '/admin/mvp-acceptance' },
    ],
  },
  {
    group: 'Documentation',
    pages: [
      { name: 'Flowcharts', path: '/flowcharts' },
      { name: 'Sitemap', path: '/sitemap' },
    ],
  },
];

export function SitemapPage(): JSX.Element {
  const total = SITEMAP.reduce((n, g) => n + g.pages.length, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sitemap"
        description={`${total} routes across ${SITEMAP.length} sections.`}
      />

      <div className="space-y-6">
        {SITEMAP.map((group) => (
          <section key={group.group}>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">{group.group}</h2>
              <span className="text-xs text-muted">{group.pages.length} pages</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {group.pages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="flex items-start justify-between gap-3 p-3 bg-white border border-border rounded-lg hover:border-nipost-green hover:shadow-sm transition-all"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{page.name}</p>
                    <p className="text-[10px] text-muted font-mono truncate">{page.path}</p>
                    {page.description ? (
                      <p className="text-xs text-gray-600 mt-1">{page.description}</p>
                    ) : null}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
